<template>
    <Modal
        id="menu-item-form-modal"
        title="menu_item_form"
        open
        @cancel="cancel"
        @submit="submit"
    >
        <div v-if="form">
            <div class="flex h-10 w-full items-center justify-end">
                <LangSwitch v-model:activeLocale="activeLocale" />
            </div>
            <TextField
                v-model="form.label[activeLocale]"
                label="label"
                name="label"
                :required="true"
            />
            <SelectField
                v-if="
                    props.context !== 'footer' &&
                    !(props.context === 'header' && props.parentId)
                "
                v-model="form.type"
                label="type"
                name="type"
                :required="true"
                :options="['link', 'dropdown']"
            />
            <PageSelectField
                v-if="form.type === 'link'"
                v-model="selectedPage"
                :pages="pages"
                :active-locale="activeLocale"
                @update:model-value="onPageSelect"
            />
            <SelectField
                v-model="form.target"
                label="target"
                name="target"
                :required="true"
                :options="['_self', '_blank', '_parent', '_top']"
            />
            <ToggleSwitch
                v-model="form.isExternal"
                label="isExternal"
                name="isExternal"
            />
        </div>
        <template #actions>
            <button
                class="btn-sm bg-primary-accent-500 hover:bg-primary-accent-600 text-white"
            >
                {{ $t('save') }}
            </button>
        </template>
    </Modal>
</template>

<script setup lang="ts">
import { emptyLocaleMap, SUPPORTED_LOCALES } from '@builder/defaults'
import { LocaleMap, Page, PlatformMenuItem } from '@builder/types'
import Modal from '@src/components/Modal.vue'
import SelectField from '@src/components/SelectField.vue'
import TextField from '@src/components/TextField.vue'
import ToggleSwitch from '@src/components/ToggleSwitch.vue'
import { ref } from 'vue'
import LangSwitch from './LangSwitch.vue'
import PageSelectField from './PageSelectField.vue'
import useBuilder from '@src/composables/builder/useBuilder'
import { onMounted } from 'vue'
import { getPages } from '@src/utils/api/pages'

const props = defineProps<{
    item?: PlatformMenuItem
    parentId?: string
    context?: 'header' | 'footer'
}>()

const form = ref<PlatformMenuItem | null>(null)
const activeLocale = ref<keyof LocaleMap>(SUPPORTED_LOCALES[0])
const pages = ref<Page[]>([])
const selectedPage = ref<Page | null>(null)
const { generateBlockKey } = useBuilder()

const onPageSelect = (page: Page) => {
    if (form.value) {
        form.value.url = page.slug ? { ...page.slug } : null
    }
}

const emit = defineEmits(['cancel', 'submit'])

const cancel = () => {
    emit('cancel')
}

const submit = () => {
    emit('submit', form.value, props.parentId)
}

const footerType =
    props.context === 'footer' ? (props.parentId ? 'link' : 'dropdown') : null

const forcedType =
    footerType ?? (props.context === 'header' && props.parentId ? 'link' : null)

onMounted(async () => {
    if (props.item) {
        form.value = { ...props.item, label: { ...props.item.label } }
    } else {
        form.value = {
            id: generateBlockKey(),
            label: emptyLocaleMap(),
            type: forcedType ?? 'link',
            url: emptyLocaleMap(),
            target: '_self',
            isExternal: false,
        }
    }

    if (forcedType && form.value) {
        form.value.type = forcedType
    }

    const pagesRequest = await getPages()
    pages.value = pagesRequest.data

    if (props.item?.url) {
        const urlValues = Object.values(props.item.url)
        selectedPage.value =
            pages.value.find(
                (p) =>
                    p.slug &&
                    Object.values(p.slug).some((s) => urlValues.includes(s))
            ) ?? null
    }
})
</script>
